/**
 * Tour Overlay Component
 *
 * Renders the guided tour step-by-step overlay with:
 * - Spotlight on target element
 * - Tooltip with content
 * - Navigation controls
 * - Progress indicator
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useGuidedTour, TourStep } from '@/contexts/GuidedTourContext';
import { useAccessibility } from '@/contexts/AccessibilityContext';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';

// ============================================================================
// TYPES
// ============================================================================

interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface TooltipPosition {
  top: number;
  left: number;
  arrowPosition: 'top' | 'bottom' | 'left' | 'right';
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getElementRect(selector: string): TargetRect | null {
  const element = document.querySelector(selector);
  if (!element) return null;

  const rect = element.getBoundingClientRect();
  return {
    top: rect.top + window.scrollY,
    left: rect.left + window.scrollX,
    width: rect.width,
    height: rect.height,
  };
}

function calculateTooltipPosition(
  targetRect: TargetRect | null,
  placement: TourStep['placement'] = 'bottom',
  tooltipWidth: number = 320,
  tooltipHeight: number = 200
): TooltipPosition {
  if (!targetRect) {
    // Center on screen
    return {
      top: window.innerHeight / 2 - tooltipHeight / 2,
      left: window.innerWidth / 2 - tooltipWidth / 2,
      arrowPosition: 'top',
    };
  }

  const padding = 16;
  const arrowSize = 12;
  const viewport = {
    width: window.innerWidth,
    height: window.innerHeight,
  };

  let top = 0;
  let left = 0;
  let arrowPosition: 'top' | 'bottom' | 'left' | 'right' = 'top';

  switch (placement) {
    case 'top':
      top = targetRect.top - tooltipHeight - arrowSize - padding;
      left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
      arrowPosition = 'bottom';
      break;

    case 'bottom':
      top = targetRect.top + targetRect.height + arrowSize + padding;
      left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
      arrowPosition = 'top';
      break;

    case 'left':
      top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
      left = targetRect.left - tooltipWidth - arrowSize - padding;
      arrowPosition = 'right';
      break;

    case 'right':
      top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
      left = targetRect.left + targetRect.width + arrowSize + padding;
      arrowPosition = 'left';
      break;

    case 'center':
    default:
      top = viewport.height / 2 - tooltipHeight / 2;
      left = viewport.width / 2 - tooltipWidth / 2;
      arrowPosition = 'top';
      break;
  }

  // Ensure tooltip stays within viewport
  if (left < padding) left = padding;
  if (left + tooltipWidth > viewport.width - padding) {
    left = viewport.width - tooltipWidth - padding;
  }
  if (top < padding) top = padding;
  if (top + tooltipHeight > viewport.height - padding) {
    top = viewport.height - tooltipHeight - padding;
  }

  return { top, left, arrowPosition };
}

// ============================================================================
// SPOTLIGHT COMPONENT
// ============================================================================

interface SpotlightProps {
  targetRect: TargetRect | null;
  padding?: number;
  onClick?: () => void;
}

function Spotlight({ targetRect, padding = 8, onClick }: SpotlightProps) {
  if (!targetRect) return null;

  const spotlightStyle = {
    position: 'absolute' as const,
    top: targetRect.top - padding,
    left: targetRect.left - padding,
    width: targetRect.width + padding * 2,
    height: targetRect.height + padding * 2,
    borderRadius: '8px',
    boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.7)',
    pointerEvents: 'none' as const,
    zIndex: 9998,
  };

  return <div style={spotlightStyle} aria-hidden="true" />;
}

// ============================================================================
// TOOLTIP COMPONENT
// ============================================================================

interface TooltipProps {
  step: TourStep;
  position: TooltipPosition;
  currentIndex: number;
  totalSteps: number;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  locale: string;
}

function Tooltip({
  step,
  position,
  currentIndex,
  totalSteps,
  onNext,
  onPrev,
  onSkip,
  locale,
}: TooltipProps) {
  const { t } = useTranslation();
  const tooltipRef = useRef<HTMLDivElement>(null);

  const title = locale === 'es' ? step.titleEs : step.title;
  const content = locale === 'es' ? step.contentEs : step.content;
  const isFirstStep = currentIndex === 0;
  const isLastStep = currentIndex === totalSteps - 1;
  const progressPercent = ((currentIndex + 1) / totalSteps) * 100;

  // Focus trap
  useEffect(() => {
    const firstButton = tooltipRef.current?.querySelector('button');
    firstButton?.focus();
  }, [step.id]);

  const arrowStyles: Record<string, React.CSSProperties> = {
    top: {
      position: 'absolute',
      top: '-8px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: 0,
      height: 0,
      borderLeft: '8px solid transparent',
      borderRight: '8px solid transparent',
      borderBottom: '8px solid white',
    },
    bottom: {
      position: 'absolute',
      bottom: '-8px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: 0,
      height: 0,
      borderLeft: '8px solid transparent',
      borderRight: '8px solid transparent',
      borderTop: '8px solid white',
    },
    left: {
      position: 'absolute',
      left: '-8px',
      top: '50%',
      transform: 'translateY(-50%)',
      width: 0,
      height: 0,
      borderTop: '8px solid transparent',
      borderBottom: '8px solid transparent',
      borderRight: '8px solid white',
    },
    right: {
      position: 'absolute',
      right: '-8px',
      top: '50%',
      transform: 'translateY(-50%)',
      width: 0,
      height: 0,
      borderTop: '8px solid transparent',
      borderBottom: '8px solid transparent',
      borderLeft: '8px solid white',
    },
  };

  return (
    <div
      ref={tooltipRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="tour-tooltip-title"
      aria-describedby="tour-tooltip-content"
      className="fixed bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-5 max-w-sm z-[9999]"
      style={{
        top: position.top,
        left: position.left,
        minWidth: '300px',
      }}
    >
      {/* Arrow */}
      <div style={arrowStyles[position.arrowPosition]} aria-hidden="true" />

      {/* Progress indicator */}
      {step.showProgress && (
        <div className="mb-3">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
            <span>
              {t('tour.step', 'Step')} {currentIndex + 1} {t('tour.of', 'of')} {totalSteps}
            </span>
            <span>{Math.round(progressPercent)}%</span>
          </div>
          <Progress value={progressPercent} className="h-1" />
        </div>
      )}

      {/* Title */}
      <h3
        id="tour-tooltip-title"
        className="text-lg font-semibold mb-2"
      >
        {title}
      </h3>

      {/* Content */}
      <p
        id="tour-tooltip-content"
        className="text-sm text-muted-foreground mb-4"
      >
        {content}
      </p>

      {/* Navigation buttons */}
      <div className="flex items-center justify-between gap-2">
        <div>
          {step.showSkipButton !== false && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onSkip}
              className="text-muted-foreground"
            >
              {t('tour.skip', 'Skip Tour')}
            </Button>
          )}
        </div>

        <div className="flex gap-2">
          {!isFirstStep && (
            <Button
              variant="outline"
              size="sm"
              onClick={onPrev}
            >
              {t('tour.back', 'Back')}
            </Button>
          )}

          <Button
            size="sm"
            onClick={onNext}
          >
            {isLastStep ? t('tour.finish', 'Finish') : t('tour.next', 'Next')}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN TOUR OVERLAY COMPONENT
// ============================================================================

export function TourOverlay() {
  const { i18n } = useTranslation();
  const {
    activeTour,
    currentStep,
    currentStepIndex,
    isRunning,
    isPaused,
    nextStep,
    prevStep,
    skipTour,
  } = useGuidedTour();
  const { settings: a11ySettings, announce } = useAccessibility();

  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition>({
    top: 0,
    left: 0,
    arrowPosition: 'top',
  });
  const [isWaitingForElement, setIsWaitingForElement] = useState(false);

  const locale = i18n?.language || 'en';

  // Find target element and calculate positions
  const updatePositions = useCallback(() => {
    if (!currentStep) return;

    const rect = getElementRect(currentStep.target);
    setTargetRect(rect);

    // If waiting for element and it's not found, keep waiting
    if (currentStep.waitForElement && !rect) {
      setIsWaitingForElement(true);
      return;
    }

    setIsWaitingForElement(false);

    const position = calculateTooltipPosition(
      rect,
      currentStep.placement
    );
    setTooltipPosition(position);

    // Scroll target into view if needed
    if (rect && !currentStep.disableScrolling) {
      const element = document.querySelector(currentStep.target);
      if (element) {
        element.scrollIntoView({
          behavior: a11ySettings.reduceMotion ? 'auto' : 'smooth',
          block: 'center',
        });
      }
    }
  }, [currentStep, a11ySettings.reduceMotion]);

  // Update positions when step changes
  useEffect(() => {
    if (isRunning && currentStep) {
      // Apply delay if specified
      const delay = currentStep.delay ?? 0;
      const timer = setTimeout(() => {
        updatePositions();
      }, delay);

      return () => clearTimeout(timer);
    }
  }, [isRunning, currentStep, updatePositions]);

  // Poll for element if waiting
  useEffect(() => {
    if (!isWaitingForElement) return;

    const interval = setInterval(() => {
      updatePositions();
    }, 500);

    return () => clearInterval(interval);
  }, [isWaitingForElement, updatePositions]);

  // Update positions on resize/scroll
  useEffect(() => {
    if (!isRunning) return;

    const handleUpdate = () => {
      updatePositions();
    };

    window.addEventListener('resize', handleUpdate);
    window.addEventListener('scroll', handleUpdate, true);

    return () => {
      window.removeEventListener('resize', handleUpdate);
      window.removeEventListener('scroll', handleUpdate, true);
    };
  }, [isRunning, updatePositions]);

  // Announce step changes for screen readers
  useEffect(() => {
    if (currentStep && isRunning) {
      const title = locale === 'es' ? currentStep.titleEs : currentStep.title;
      announce(title, 'polite');
    }
  }, [currentStep, isRunning, locale, announce]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isRunning) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          skipTour();
          break;
        case 'ArrowRight':
        case 'Enter':
          nextStep();
          break;
        case 'ArrowLeft':
          prevStep();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isRunning, nextStep, prevStep, skipTour]);

  // Don't render if tour is not running or paused
  if (!isRunning || isPaused || !activeTour || !currentStep) {
    return null;
  }

  // Show loading state while waiting for element
  if (isWaitingForElement) {
    return createPortal(
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50"
        aria-live="polite"
      >
        <div className="bg-white dark:bg-gray-900 rounded-lg p-6 text-center">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">
            {locale === 'es' ? 'Cargando...' : 'Loading...'}
          </p>
        </div>
      </div>,
      document.body
    );
  }

  return createPortal(
    <>
      {/* Overlay backdrop */}
      {!currentStep.disableOverlay && (
        <div
          className="fixed inset-0 z-[9997]"
          onClick={skipTour}
          aria-hidden="true"
        />
      )}

      {/* Spotlight on target */}
      <Spotlight
        targetRect={targetRect}
        padding={currentStep.spotlightPadding ?? 8}
      />

      {/* Tooltip */}
      <Tooltip
        step={currentStep}
        position={tooltipPosition}
        currentIndex={currentStepIndex}
        totalSteps={activeTour.steps.length}
        onNext={nextStep}
        onPrev={prevStep}
        onSkip={skipTour}
        locale={locale}
      />
    </>,
    document.body
  );
}

// ============================================================================
// TOUR LAUNCHER COMPONENT
// ============================================================================

export function TourLauncher() {
  const { t, i18n } = useTranslation();
  const {
    availableTours,
    getRecommendedTours,
    startTour,
    hasCompletedTour,
    isRunning,
  } = useGuidedTour();
  const [isOpen, setIsOpen] = useState(false);
  const locale = i18n?.language || 'en';

  // For demo purposes, show all tours
  const tours = availableTours;

  if (isRunning) return null;

  return (
    <div className="relative" data-tour="help-button">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t('tour.helpButton', 'Help & Tours')}
        aria-expanded={isOpen}
      >
        <span className="mr-1" aria-hidden="true">?</span>
        {t('tour.help', 'Help')}
      </Button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 top-full mt-2 w-72 bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="font-semibold">
                {t('tour.availableTours', 'Available Tours')}
              </h3>
              <p className="text-xs text-muted-foreground">
                {t('tour.toursDescription', 'Interactive guides to help you learn')}
              </p>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {tours.map(tour => {
                const isCompleted = hasCompletedTour(tour.id);
                const name = locale === 'es' ? tour.nameEs : tour.name;
                const description = locale === 'es' ? tour.descriptionEs : tour.description;

                return (
                  <button
                    key={tour.id}
                    onClick={() => {
                      startTour(tour.id);
                      setIsOpen(false);
                    }}
                    className="w-full p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800 flex items-start gap-3 border-b border-gray-100 dark:border-gray-800 last:border-0"
                  >
                    <span
                      className={`text-lg flex-shrink-0 ${isCompleted ? 'opacity-50' : ''}`}
                      aria-hidden="true"
                    >
                      {tour.category === 'onboarding' ? '🎯' :
                       tour.category === 'workflow' ? '📋' :
                       tour.category === 'feature' ? '✨' : '❓'}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`font-medium text-sm ${isCompleted ? 'line-through opacity-50' : ''}`}>
                          {name}
                        </span>
                        {isCompleted && (
                          <span className="text-green-600 text-xs">✓</span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {description}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        ~{tour.estimatedMinutes} {t('tour.minutes', 'min')}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default TourOverlay;
